/**
 * Save-the-date helpers shared by the public event page templates (JUB-94).
 *
 * Builds the "add to calendar" targets for the save-the-date buttons:
 * - Google Calendar template URL (opened in a new tab)
 * - Apple/other calendars via a downloadable .ics file (RFC 5545 VEVENT)
 */

import type { EventCeremony } from "@/services/api/eventSettings";
import { getCountryDisplayName } from "@/utils/eventUtils";

/** Minimal structural slice of PublicEvent needed to build calendar entries. */
export type SaveTheDateEvent = {
  name: string;
  date?: string | null;
  description?: string;
  url?: string;
  country?: string;
  ceremonies?: {
    civil?: EventCeremony;
    religious?: EventCeremony;
    party?: EventCeremony;
    baptism?: EventCeremony;
  };
};

/** Extract the date part (YYYYMMDD) of the event date for an all-day entry. */
function toAllDayStamp(date: string): string | null {
  const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}${isoMatch[2]}${isoMatch[3]}`;
  }
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

/** Day after the event, YYYYMMDD (exclusive DTEND for all-day events). */
function nextDayStamp(stamp: string): string {
  const y = Number(stamp.slice(0, 4));
  const m = Number(stamp.slice(4, 6));
  const d = Number(stamp.slice(6, 8));
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return `${next.getUTCFullYear()}${String(next.getUTCMonth() + 1).padStart(2, "0")}${String(
    next.getUTCDate()
  ).padStart(2, "0")}`;
}

/** Human-readable location: first enabled ceremony venue/address/city + country name. */
export function buildEventLocation(event: SaveTheDateEvent, locale: string): string {
  const ceremonies = [
    event.ceremonies?.civil,
    event.ceremonies?.religious,
    event.ceremonies?.baptism,
    event.ceremonies?.party,
  ].filter((c): c is EventCeremony => Boolean(c?.enabled));

  const parts: string[] = [];
  const withVenue = ceremonies.find((c) => c.venue || c.address || c.city);
  if (withVenue) {
    for (const value of [withVenue.venue, withVenue.address, withVenue.city]) {
      if (value) parts.push(value);
    }
  }

  const countryName = getCountryDisplayName(event.country, locale);
  if (countryName) parts.push(countryName);

  return parts.join(", ");
}

/**
 * Google Calendar "add event" template URL for the event (all-day entry).
 * Returns null when the event has no usable date.
 */
export function buildGoogleCalendarUrl(event: SaveTheDateEvent, locale: string): string | null {
  if (!event.date) return null;
  const start = toAllDayStamp(event.date);
  if (!start) return null;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.name,
    dates: `${start}/${nextDayStamp(start)}`,
  });
  if (event.description) params.set("details", event.description);
  const location = buildEventLocation(event, locale);
  if (location) params.set("location", location);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Escape text per RFC 5545 (commas, semicolons, backslashes, newlines). */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Build the .ics (text/calendar) content for the event as an all-day VEVENT.
 * Returns null when the event has no usable date.
 */
export function buildIcsContent(event: SaveTheDateEvent, locale: string): string | null {
  if (!event.date) return null;
  const start = toAllDayStamp(event.date);
  if (!start) return null;

  const now = new Date();
  const dtstamp = `${now.getUTCFullYear()}${String(now.getUTCMonth() + 1).padStart(2, "0")}${String(
    now.getUTCDate()
  ).padStart(2, "0")}T${String(now.getUTCHours()).padStart(2, "0")}${String(
    now.getUTCMinutes()
  ).padStart(2, "0")}${String(now.getUTCSeconds()).padStart(2, "0")}Z`;

  const uid = `${(event.url || event.name).replace(/[^A-Za-z0-9-]/g, "")}-${start}@wedistar.com`;
  const location = buildEventLocation(event, locale);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Wedistar//Jubiel//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;VALUE=DATE:${start}`,
    `DTEND;VALUE=DATE:${nextDayStamp(start)}`,
    `SUMMARY:${escapeIcsText(event.name)}`,
  ];
  if (event.description) lines.push(`DESCRIPTION:${escapeIcsText(event.description)}`);
  if (location) lines.push(`LOCATION:${escapeIcsText(location)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");

  return lines.join("\r\n");
}

/** Trigger a browser download of the event as a .ics file. Returns false when no date. */
export function downloadIcsFile(event: SaveTheDateEvent, locale: string): boolean {
  const content = buildIcsContent(event, locale);
  if (!content) return false;

  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = `${(event.url || "save-the-date").replace(/^@/, "")}.ics`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
  return true;
}
